
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions } from 'vuex';
    import hash from 'hash-it';
    import { AxiosResponse } from 'axios';
    import Interval from '@/helper/Interval';
    import api from '@/services/Api';
    import { DetailApi, DetailConfig, DetailLoaders, DetailParams, FwNotification } from '@/types';
    import { VDetail, VDialog, VForm } from '@/types/Components';
    import { confirmSwal, dirtySwal, questionSwal, successSwal } from '@/helper/swals';

    @Component({
        methods: {
            ...mapActions('grids', ['loadGrid', 'saveGrid']),
        },
    })
    export default class Detail<
        T extends { isPersistent: boolean },
        R extends { body: Partial<T>, notifications: FwNotification[] },
        S extends Record<string, unknown>,
    > extends Vue implements VDetail<T, S> {
        @Prop({ required: true }) public config!: DetailConfig<T>;
        @Prop({ default: () => ({}) }) public params!: DetailParams<T>;

        public $refs!: {
            dialog: VDialog;
            form: VForm;
            refresh: Vue;
        };

        public data: T | Partial<T> = {};
        public selections: S = {} as S;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        public validator: any = {};
        public permissions: string[] = [];
        public flags: { readonly?: boolean; readonlyUpdateFields?: string[]; } = { readonly: false };
        public notifications: FwNotification[] = [];

        protected initialLoad = true;
        protected initialDataHash = '';
        protected hasChangedInterval = new Interval(60 * 1000, this.checkHasChanged);

        public loaders: DetailLoaders = {
            loading: false,
            loadingManualRefresh: false,
            saving: false,
            deleting: false,
        }

        public get title(): string {
            return this.config.title ?? 'Daten';
        }

        public get isSaveButtonVisible(): boolean {
            return this.config.buttons?.save?.visible ?? true;
        }

        public get isDeleteButtonVisible(): boolean {
            return this.config.buttons?.delete?.visible ?? true;
        }

        public get remoteApi(): DetailApi {
            let remote = this.config.remote;

            if (this.config.remote.indexOf('/') === this.config.remote.lastIndexOf('/')) {
                remote += '/detail';
            }

            return {
                load: `${remote}/load`,
                save: `${remote}/save`,
                destroy: `${remote}/delete`,
                validator: `${remote}/validator`,
                haschanged: `${remote}/haschanged`,
            };
        }

        public get isPersistent(): boolean {
            return this.data?.isPersistent === true;
        }

        public get isDirty(): boolean {
            return this.initialDataHash.length > 0
                ? this.initialDataHash !== hash(this.data).toString(16)
                : false;
        }

        public get isReadonly(): boolean {
            return this.flags?.readonly ?? false;
        }

        public get formBindings(): unknown {
            return {
                data: this.data,
                selections: this.selections,
                permissions: this.permissions,
                validator: this.validator,
                loaders: this.loaders,
                isDirty: this.isDirty,
                isReadonly: this.isReadonly,
                isFieldReadonly: this.isFieldReadonly,
            };
        }

        public get actionBindings(): unknown {
            return {
                data: this.data,
                selections: this.selections,
                permissions: this.permissions,
                loaders: this.loaders,
                isDirty: this.isDirty,
                isReadonly: this.isReadonly,
                isFieldReadonly: this.isFieldReadonly,
                isPersistent: this.isPersistent,
                callAction: this.callAction,
            };
        }

        protected isFieldReadonly(field: string): boolean {
            if (this.isReadonly) {
                return true;
            }

            if ((this.flags?.readonlyUpdateFields?.length ?? 0) === 0) {
                return false;
            }

            return this.flags.readonlyUpdateFields?.indexOf(field) === -1;
        }

        @Watch('$attrs.value')
        protected onChangeVisible(visible: boolean): void {
            if (visible) {
                this.initialLoad = true;

                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }

                const DataClass = this.config.class;

                this.data = DataClass
                    ? new DataClass()
                    : {};

                this.notifications = [];

                this.load();
                this.fetchValidator();
            }

            this.hasChangedInterval.stop();
        }

        protected mounted(): void {
            for (const actionKey of Object.keys(this.config.actions ?? {})) {
                this.$set(this.loaders, actionKey, false);
            }
        }

        protected destroyed(): void {
            this.hasChangedInterval.stop();
        }

        protected onClickSave(): void {
            const isValid = this.$refs.form.validate();

            if (isValid) {
                if (this.isPersistent) {
                    this.hasChangedInterval.stop();
                    this.checkHasChanged(true);
                } else {
                    this.save();
                }
            }

            this.$emit('click:save', isValid);
        }

        protected onClickDelete(): void {
            confirmSwal('Löschen', `${this.title} endgültig löschen?`, 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }) => {
                    if (isConfirmed) {
                        this.delete();
                    }

                    this.$emit('click:delete', isConfirmed);
                });
        }

        protected onClickRefresh(): void {
            if (this.isDirty) {
                dirtySwal().then(({ isDismissed }) => {
                    if (isDismissed) {
                        this.loaders.loadingManualRefresh = true;
                        this.load();
                    }

                    this.$emit('click:refresh', isDismissed);
                });
            } else {
                this.loaders.loadingManualRefresh = true;
                this.load();
                this.$emit('click:refresh', true);
            }
        }

        protected onClickClose(): void {
            if (this.isDirty) {
                dirtySwal().then(({ isDismissed }) => isDismissed && this.$emit('close'));
            } else {
                this.$emit('close');
            }
        }

        protected load(): void {
            this.loaders.loading = true;

            api.post(this.remoteApi.load, this.params)
                .then(({ data: { body, notifications } }: AxiosResponse<R>) => {
                    const DataClass = this.config.class;

                    this.data = DataClass
                        ? new DataClass(body)
                        : body;

                    this.notifications = notifications;

                    this.initialDataHash = hash(this.data).toString(16);

                    this.$emit('load', {
                        data: this.data,
                        notifications: this.notifications,
                    });

                    if (this.$refs.form) {
                        this.$refs.form.resetValidation();
                    }

                    this.hasChangedInterval.stop();

                    if (this.isPersistent) {
                        this.hasChangedInterval.start();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.$emit('load:error', error);
                })
                .finally(() => {
                    this.initialLoad = false;
                    this.loaders.loading = false;
                    this.loaders.loadingManualRefresh = false;
                });
        }

        protected save(): void {
            this.loaders.saving = true;

            api.post(this.remoteApi.save, this.data)
                .then(({ data: { body, notifications } }: AxiosResponse<R>) => {
                    const DataClass = this.config.class;

                    this.data = DataClass
                        ? new DataClass(body)
                        : body;

                    this.notifications = notifications;

                    this.initialDataHash = hash(this.data).toString(16);

                    this.$emit('save', {
                        data: this.data,
                        notifications: this.notifications,
                    });

                    if (this.$refs.form) {
                        this.$refs.form.resetValidation();
                    }

                    this.hasChangedInterval.stop();

                    if (this.isPersistent) {
                        this.hasChangedInterval.start();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.$emit('save:error', error);
                })
                .finally(() => {
                    this.loaders.saving = false;
                });
        }

        protected delete(): void {
            this.loaders.deleting = true;

            api.post(this.remoteApi.destroy, this.data)
                .then(() => {
                    this.$emit('delete');
                    this.$emit('close');
                })
                .catch((error) => {
                    console.error(error);
                    this.$emit('delete:error', error);
                })
                .finally(() => {
                    this.loaders.deleting = false;
                });
        }

        protected fetchValidator(): void {
            api.post(this.remoteApi.validator)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .then(({ data: { body } }: AxiosResponse<{ body: any }>) => {
                    this.validator = body;
                })
                .catch(console.error);
        }

        protected checkHasChanged(isAttemptingSave?: boolean): void {
            if (isAttemptingSave) {
                this.loaders.saving = true;
            }

            type HasChangedResponse = { body: { result: boolean; fw_modified_user: string, fw_modified?: string } };

            api.post(this.remoteApi.haschanged, this.data)
                .then(({ data: { body } }: AxiosResponse<HasChangedResponse>) => {
                    if (body.result !== false) {
                        const info = `${body.fw_modified_user ?? '-'} / ${this.$dt(body.fw_modified ?? '-')}`;

                        confirmSwal(
                            'Datensatz Änderung',
                            isAttemptingSave
                                ? `Datensatz wurde geändert. Wollen Sie den Datensatz trotzdem speichern? (${info})`
                                : `Datensatz wurde geändert. Wollen Sie den Datensatz neu laden? (${info})`,
                            isAttemptingSave ? 'Speichern' : 'Ok',
                            isAttemptingSave ? 'Änderungen verwerfen' : 'Abbrechen',
                        ).then((res) => {
                            if (res.value && isAttemptingSave) {
                                this.save();
                            } else if ((res.value && !isAttemptingSave) || isAttemptingSave) {
                                this.load();
                                this.loaders.saving = false;
                            }
                        });
                    } else if (isAttemptingSave && this.isPersistent) {
                        this.save();
                    } else {
                        this.loaders.saving = false;
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.loaders.saving = false;
                });
        }

        protected callAction(actionKey: string): void {
            const action = this.config.actions ? this.config.actions[actionKey] : null;
            if (!action) {
                return console.warn(`Missing action '${actionKey}' in detail config.`);
            }

            const apiCall = (): void => {
                this.loaders[actionKey] = true;

                api.post(action.action, this.params)
                    .then(({ data: { body, notifications } }: AxiosResponse<R>) => {
                        const DataClass = this.config.class;

                        this.data = DataClass
                            ? new DataClass(body)
                            : body;

                        this.notifications = notifications;

                        this.initialDataHash = hash(this.data).toString(16);

                        this.$emit('load', {
                            data: this.data,
                            notifications: this.notifications,
                        });

                        if (this.$refs.form) {
                            this.$refs.form.resetValidation();
                        }

                        this.hasChangedInterval.stop();

                        if (this.isPersistent) {
                            this.hasChangedInterval.start();
                        }

                        const isSuccess = action.success && this.notifications.length === 0;
                        if (isSuccess) {
                            successSwal(this.title, action.success);
                        }

                        this.$emit(`action:${actionKey}`, this.data, isSuccess);
                    })
                    .catch((error) => {
                        console.error(error);
                        this.$emit(`action-error:${actionKey}`, error);
                    })
                    .finally(() => {
                        this.loaders[actionKey] = false;
                    });
            };

            if (action.confirm) {
                questionSwal(
                    action.confirm.title,
                    action.confirm.text,
                    action.confirm.yes,
                    action.confirm.no,
                ).then(({ value }) => {
                    if (value === true) {
                        apiCall();
                    }
                });
            } else {
                apiCall();
            }
        }

        protected notificationType(notification: FwNotification): string | undefined {
            switch (notification.class) {
                case 'Success':
                    return 'success';
                case 'Warning':
                    return 'warning';
                case 'Error':
                    return 'error';
                case 'Notice':
                    return 'info';
                default:
                    return undefined;
            }
        }
    }
